import {
  messageIconForAttachments,
  type ChatRowMessageIcon,
  type MessageIconAttachment,
} from './messageIconForAttachments';

/**
 * The chat-list row fields an outgoing message makes true — TA-10878.
 *
 * Two places have to agree on these, which is why they are computed once
 * here rather than inline at each call site:
 *
 *  1. `bumpChatToTop`, which writes them onto the Chat entity;
 *  2. the `listDraftMessages` cache write that stages the draft, whose query
 *     selects `chat { ...ChatDetail }` — including `messageIcon`,
 *     `messageBody` and `recentActivityAt`. The composer captured that chat
 *     object BEFORE the send, so writing it back normalizes a pre-send
 *     snapshot onto the Chat entity and undoes the bump. That is what put the
 *     previous message's attachment icon back on the row: `messageBody` was
 *     then repaired by the `chatUpdate` subscription (which carries a body
 *     but no icon), so only the icon stayed wrong, one message behind.
 */
export type ChatRowFieldsForSend = {
  recentActivityAt: string;
  messageBody?: string | null;
  messageIcon: ChatRowMessageIcon;
  unreadCount?: number;
};

export const chatRowFieldsForSend = ({
  body,
  createdAt,
  attachments,
  fromOpenChat,
}: {
  body?: string | null;
  createdAt?: string | null;
  attachments?: readonly (MessageIconAttachment | null | undefined)[] | null;
  /**
   * TA-9295: only a message composed inside the open thread proves the user
   * has seen the chat, so only then may the row's badge be zeroed.
   */
  fromOpenChat?: boolean;
}): ChatRowFieldsForSend => ({
  recentActivityAt: createdAt || new Date().toISOString(),
  messageBody: body,
  messageIcon: messageIconForAttachments(attachments),
  ...(fromOpenChat ? { unreadCount: 0 } : {}),
});
